/**
 * Play Archive → custom stat columns (FR-ARCHIVE-01, FR-STATS-01).
 *
 * The team's custom fields are OPTIONAL archive columns (registered in
 * `archive-columns.ts`, hidden by default, turned on in the column picker).
 * Three rules:
 *   1. **Retired fields still show.** A field marked inactive is dropped from
 *      Play Entry but keeps its column here, because the values it collected
 *      are part of the game record (AC-STATS-004).
 *   2. **Values come from the active version.** `PlayView.custom_stats` is the
 *      projection of `play_versions.stat_payload.custom_stats` for
 *      `active_version_id`, so a losing version never leaks into the table.
 *   3. **Visibility is a per-user preference** (`archive-layout.ts`), not URL
 *      state: a column choice should not change what a shared link shows.
 */
import type { PlayView, StatFieldDefinition } from '../../lib/api-types';

/** Column ids are namespaced so they cannot collide with a built-in column. */
export const CUSTOM_COLUMN_PREFIX = 'custom:';

export function toCustomColumnId(statKey: string): string {
  return `${CUSTOM_COLUMN_PREFIX}${statKey}`;
}

export interface CustomColumnChoice {
  /** `custom:missed_tackles` */
  id: string;
  statKey: string;
  label: string;
  /** Retired fields are annotated in the picker so nobody re-adds one by mistake. */
  isRetired: boolean;
  isVisible: boolean;
}

/**
 * The picker model: every custom field, in registry order, with its current
 * visibility. Built-ins are excluded — they already have fixed columns.
 */
export function buildCustomColumnChoices(
  fields: readonly StatFieldDefinition[],
  visibleKeys: readonly string[]
): CustomColumnChoice[] {
  return fields
    .filter((field) => !field.is_builtin)
    .slice()
    .sort((left, right) =>
      left.display_order === right.display_order
        ? left.label.localeCompare(right.label)
        : left.display_order - right.display_order
    )
    .map((field) => ({
      id: toCustomColumnId(field.stat_key),
      statKey: field.stat_key,
      label: field.label,
      isRetired: !field.is_enabled,
      isVisible: visibleKeys.includes(field.stat_key),
    }));
}

/** Raw `custom_stats` bag off a play's active version. */
function customStatsOf(play: PlayView): Record<string, unknown> {
  const payload = (play as { custom_stats?: unknown }).custom_stats;
  return typeof payload === 'object' && payload !== null && !Array.isArray(payload)
    ? (payload as Record<string, unknown>)
    : {};
}

/** Em dash for "not recorded", so a blank cell is never mistaken for a zero. */
export const NOT_RECORDED = '—';

/**
 * Render one custom value for the table. Formatting follows `data_type`:
 * booleans read Yes/No, tag lists join with commas, numbers keep their
 * precision, and anything unexpected degrades to its string form rather than
 * crashing the row.
 */
export function formatCustomStatValue(play: PlayView, field: StatFieldDefinition): string {
  const value = customStatsOf(play)[field.stat_key];
  if (value === undefined || value === null || value === '') return NOT_RECORDED;

  if (field.data_type === 'boolean') {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return value === 'true' ? 'Yes' : 'No';
  }
  if (field.data_type === 'tag_list') {
    if (Array.isArray(value)) {
      const tags = value.filter((tag): tag is string => typeof tag === 'string');
      return tags.length === 0 ? NOT_RECORDED : tags.join(', ');
    }
    return String(value);
  }
  if (field.data_type === 'enum') {
    const option = (field.enum_values ?? []).find((choice) => choice.value === String(value));
    return option?.label ?? String(value);
  }
  if (field.data_type === 'numeric') {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? String(parsed) : NOT_RECORDED;
  }
  return String(value);
}

/**
 * The fields whose columns are visible, in registry order. Keys with no matching
 * field (a stat retired AND deleted, or a stale preference) are skipped.
 */
export function visibleCustomFields(
  fields: readonly StatFieldDefinition[],
  visibleKeys: readonly string[]
): StatFieldDefinition[] {
  return fields.filter((field) => !field.is_builtin && visibleKeys.includes(field.stat_key));
}

/** Toggle one column, returning the next list of visible stat keys. */
export function toggleCustomColumn(visibleKeys: readonly string[], statKey: string): string[] {
  return visibleKeys.includes(statKey)
    ? visibleKeys.filter((key) => key !== statKey)
    : [...visibleKeys, statKey];
}
